<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { getProductionDailyOutput } from '../productService'
import type { ProductionDailyOutputRow } from '../types'
import { buildDailyOutputCsv, filterDailyOutputRows, summarizeDailyOutput } from '../productionReporting'

const client = useApiClient()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const today = new Date().toLocaleDateString('en-CA')
const reportDate = ref(typeof route.query.date === 'string' ? route.query.date : today)
const dailyRows = ref<ProductionDailyOutputRow[]>([])
const loading = ref(false)
const error = ref('')
const stageFilter = ref(typeof route.query.stage === 'string' ? route.query.stage : '')
const employeeFilter = ref(typeof route.query.employee === 'string' ? route.query.employee : '')
const exportMessage = ref('')

const reportingLabels = computed(() => ({
  unassigned: t('engineering.productionOrder.unassigned'),
  productionOutputTitle: t('engineering.productionOrder.dailyOutputTitle'),
  csvDate: t('engineering.productionOrder.date'),
  csvFilters: t('engineering.productionOrder.csvFilters'),
  csvRows: t('engineering.productionOrder.rows'),
  csvCompleted: t('engineering.productionOrder.completed'),
  csvRejected: t('engineering.productionOrder.rejected'),
  csvRework: t('engineering.productionOrder.rework'),
  csvSequence: t('engineering.productionOrder.sequence'),
  csvStage: t('engineering.productionOrder.stage'),
  csvStageCode: t('engineering.productionOrder.csvStageCode'),
  csvEmployee: t('engineering.productionOrder.employee'),
  csvEmployeeId: t('engineering.productionOrder.csvEmployeeId'),
}))

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  exportMessage.value = ''
  try {
    const result = await getProductionDailyOutput(client, reportDate.value)
    dailyRows.value = result.rows
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

let queryTimer: ReturnType<typeof setTimeout> | undefined

function syncQuery(): void {
  if (queryTimer) clearTimeout(queryTimer)
  queryTimer = setTimeout(() => {
    void router.replace({
      query: {
        date: reportDate.value || undefined,
        stage: stageFilter.value || undefined,
        employee: employeeFilter.value || undefined,
      },
    })
  }, 0)
}

watch([reportDate, stageFilter, employeeFilter], syncQuery)
watch(reportDate, () => { void load() })
watch(() => route.query, (query) => {
  const nextDate = typeof query.date === 'string' ? query.date : today
  const nextStage = typeof query.stage === 'string' ? query.stage : ''
  const nextEmployee = typeof query.employee === 'string' ? query.employee : ''
  if (reportDate.value !== nextDate) reportDate.value = nextDate
  if (stageFilter.value !== nextStage) stageFilter.value = nextStage
  if (employeeFilter.value !== nextEmployee) employeeFilter.value = nextEmployee
})
onMounted(() => { void load() })

const stageOptions = computed(() =>
  [...new Map(dailyRows.value.map((row) => [row.stage_code, { code: row.stage_code, name: row.stage_name }])).values()].sort((left, right) => left.name.localeCompare(right.name)),
)
const employeeOptions = computed(() =>
  [...new Map(dailyRows.value.map((row) => [
    String(row.employee_id ?? 'unassigned'),
    { id: String(row.employee_id ?? 'unassigned'), name: row.employee_name || t('engineering.productionOrder.unassigned') },
  ])).values()].sort((left, right) => left.name.localeCompare(right.name)),
)
const visibleRows = computed(() => filterDailyOutputRows(dailyRows.value, stageFilter.value, employeeFilter.value))
const summary = computed(() => summarizeDailyOutput(visibleRows.value))
const filterDescription = computed(() => {
  const stage = stageOptions.value.find((option) => option.code === stageFilter.value)?.name || t('engineering.productionOrder.allStages')
  const employee = employeeOptions.value.find((option) => option.id === employeeFilter.value)?.name || t('engineering.productionOrder.allEmployees')
  return t('engineering.productionOrder.filterStageEmployee', { stage, employee })
})

function exportDailyOutput(): void {
  exportMessage.value = ''
  if (!visibleRows.value.length) {
    exportMessage.value = t('engineering.productionOrder.dailyExportEmpty')
    return
  }
  const csv = buildDailyOutputCsv(visibleRows.value, reportDate.value, filterDescription.value, reportingLabels.value)
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `production-daily-output-${reportDate.value}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
  exportMessage.value = t('engineering.productionOrder.dailyExportSuccess', { count: visibleRows.value.length })
}

function printReport(): void {
  window.print()
}
</script>

<template>
  <div class="page">
    <RouterLink class="back" to="/production-orders">{{ t('engineering.productionOrder.backToOrders') }}</RouterLink>
    <header>
      <div>
        <p class="eyebrow">{{ t('engineering.productionOrder.reportEyebrow') }}</p>
        <h2>{{ t('engineering.productionOrder.dailyOutputTitle') }}</h2>
        <p>{{ t('engineering.productionOrder.reportDescription') }}</p>
      </div>
      <div class="actions">
        <button class="secondary" type="button" :disabled="!visibleRows.length" @click="exportDailyOutput">{{ t('engineering.productionOrder.exportCsv') }}</button>
        <button class="primary" type="button" :disabled="!visibleRows.length" @click="printReport">{{ t('engineering.productionOrder.printReport') }}</button>
      </div>
    </header>
    <section class="filters">
      <label>{{ t('engineering.productionOrder.reportDate') }}<input v-model="reportDate" type="date"></label>
      <label>{{ t('engineering.productionOrder.stage') }}<select v-model="stageFilter"><option value="">{{ t('engineering.productionOrder.allStages') }}</option><option v-for="option in stageOptions" :key="option.code" :value="option.code">{{ option.name }}</option></select></label>
      <label>{{ t('engineering.productionOrder.employee') }}<select v-model="employeeFilter"><option value="">{{ t('engineering.productionOrder.allEmployees') }}</option><option v-for="option in employeeOptions" :key="option.id" :value="option.id">{{ option.name }}</option></select></label>
    </section>
    <p v-if="exportMessage" class="inline-message" role="status">{{ exportMessage }}</p>
    <div v-if="loading" class="state">{{ t('engineering.productionOrder.loadingDailyOutput') }}</div>
    <div v-else-if="error" class="state error">{{ error }}<button type="button" @click="load">{{ t('engineering.productionOrder.retry') }}</button></div>
    <div v-else-if="!dailyRows.length" class="state">{{ t('engineering.productionOrder.noDailyOutputForDate') }}</div>
    <template v-else>
      <section class="summary">
        <article><span>{{ t('engineering.productionOrder.completed') }}</span><strong>{{ summary.completed }}</strong></article>
        <article><span>{{ t('engineering.productionOrder.rejected') }}</span><strong>{{ summary.rejected }}</strong></article>
        <article><span>{{ t('engineering.productionOrder.rework') }}</span><strong>{{ summary.rework }}</strong></article>
        <article><span>{{ t('engineering.productionOrder.rows') }}</span><strong>{{ visibleRows.length }}</strong></article>
      </section>
      <div v-if="!visibleRows.length" class="state">{{ t('engineering.productionOrder.noDailyOutputForFilters') }}</div>
      <section v-else class="report-card print-surface">
        <div class="print-header">
          <div>
            <p class="eyebrow">{{ t('engineering.productionOrder.printEngineering') }}</p>
            <h3>{{ t('engineering.productionOrder.dailyOutputTitle') }}</h3>
          </div>
          <dl>
            <div><dt>{{ t('engineering.productionOrder.date') }}</dt><dd>{{ reportDate }}</dd></div>
            <div><dt>{{ t('engineering.productionOrder.stage') }}</dt><dd>{{ stageOptions.find((option) => option.code === stageFilter)?.name || t('engineering.productionOrder.allStages') }}</dd></div>
            <div><dt>{{ t('engineering.productionOrder.employee') }}</dt><dd>{{ employeeOptions.find((option) => option.id === employeeFilter)?.name || t('engineering.productionOrder.allEmployees') }}</dd></div>
          </dl>
        </div>
        <div class="summary summary--print">
          <article><span>{{ t('engineering.productionOrder.completed') }}</span><strong>{{ summary.completed }}</strong></article>
          <article><span>{{ t('engineering.productionOrder.rejected') }}</span><strong>{{ summary.rejected }}</strong></article>
          <article><span>{{ t('engineering.productionOrder.rework') }}</span><strong>{{ summary.rework }}</strong></article>
          <article><span>{{ t('engineering.productionOrder.rows') }}</span><strong>{{ visibleRows.length }}</strong></article>
        </div>
        <div class="table-wrap">
          <table>
            <thead><tr><th>{{ t('engineering.productionOrder.sequence') }}</th><th>{{ t('engineering.productionOrder.stage') }}</th><th>{{ t('engineering.productionOrder.employee') }}</th><th>{{ t('engineering.productionOrder.completed') }}</th><th>{{ t('engineering.productionOrder.rejected') }}</th><th>{{ t('engineering.productionOrder.rework') }}</th></tr></thead>
            <tbody>
              <tr v-for="(row, index) in visibleRows" :key="`${row.stage_code}-${row.employee_id ?? 'unassigned'}-${index}`">
                <td>{{ row.operation_sequence }}</td>
                <td><strong>{{ row.stage_name }}</strong><small>{{ row.stage_code }}</small></td>
                <td>{{ row.employee_name || t('engineering.productionOrder.unassigned') }}</td>
                <td>{{ row.completed_quantity }}</td>
                <td>{{ row.rejected_quantity }}</td>
                <td>{{ row.rework_quantity }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<style scoped>
.page{display:grid;gap:14px}.back{display:inline-block;color:#285b9a;font-weight:700}.eyebrow{margin:0;color:#66726e;font-size:12px;font-weight:800;text-transform:uppercase}header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px}h2,h3{margin:4px 0}.actions{display:flex;gap:8px}.primary,.secondary,.state button{padding:10px 14px;border:0;color:#fff;font-weight:700;cursor:pointer}.primary{background:#07996f}.secondary,.state button{background:#3568a8}.primary:disabled,.secondary:disabled{cursor:not-allowed;opacity:.55}.filters,.report-card{padding:18px;border:1px solid #d7dfdc;background:#fff}.filters{display:grid;grid-template-columns:repeat(3,minmax(160px,1fr));gap:10px}.filters label{display:grid;gap:4px;color:#53625d;font-size:12px;font-weight:700}input,select{padding:9px 10px;border:1px solid #b9c5c1}.inline-message{margin:0;color:#236451;font-size:13px;font-weight:700}.state{display:grid;min-height:220px;place-items:center;padding:18px;background:#fff}.error{color:#852d22}.summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}.summary article{display:flex;align-items:center;justify-content:space-between;padding:12px;border:1px solid #e1e7e5;background:#f8faf9}.summary span{color:#66726e;font-size:12px;font-weight:700}.summary strong{font-size:20px}.print-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:14px}.print-header dl{display:grid;grid-template-columns:repeat(3,minmax(120px,1fr));gap:10px;margin:0}.print-header dt{color:#66726e;font-size:12px;text-transform:uppercase}.print-header dd{margin:4px 0 0;font-weight:700}.table-wrap{overflow-x:auto}.table-wrap td:nth-last-child(-n+3){font-variant-numeric:tabular-nums}table{width:100%;border-collapse:collapse}th,td{padding:11px;border-bottom:1px solid #e1e7e5;text-align:left}th{color:#53625d;font-size:12px;text-transform:uppercase}td strong,td small{display:block}td small{margin-top:2px;color:#66726e}.summary--print{margin-bottom:14px}@media(max-width:800px){header,.print-header{align-items:stretch;flex-direction:column}.actions{flex-wrap:wrap}.filters,.summary,.print-header dl{grid-template-columns:1fr}}@media print{.back,header,.filters,.inline-message{display:none!important}.page{display:block}.summary{margin:0 0 12px}.report-card{padding:0;border:0}.print-surface{break-inside:avoid}body{background:#fff}}
</style>
